/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild, inject } from '@angular/core';
import { LucideDynamicIcon, LucideIconInput, LucidePause, LucidePlay, LucideVolume1, LucideVolume2, LucideVolumeX } from '@lucide/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { MediaUploadData, SingleUrlContent } from '../../../models';
import { SingleMediaPlaybackService } from '../../../services/single-media-playback.service';

@Component({
  selector: 'ptl-v3-resource-audio-preview',
  standalone: true,
  imports: [LucideDynamicIcon, TranslocoModule],
  templateUrl: './v3-audio-preview.component.html',
  styleUrls: ['./v3-audio-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V3ResourceAudioPreviewComponent {
  /** Receives the EditorContent object */
  @Input() content!: MediaUploadData;

  @ViewChild('audioElement') private audioElementRef?: ElementRef<HTMLAudioElement>;

  isPlaying = false;
  currentTime = 0;
  duration = 0;
  volume = 1;
  isMuted = false;

  constructor() {
    inject(SingleMediaPlaybackService).start();
  }

  get audioUrl(): string {
    const urlContent = this.content?.content as SingleUrlContent | undefined;
    return urlContent?.url ?? '';
  }

  get position(): string {
    return this.content?.position || 'CENTER';
  }

  get canSeek(): boolean {
    return this.duration > 0;
  }

  get progressPct(): number {
    if (!this.canSeek) {
      return 0;
    }
    return Math.min(100, (this.currentTime / this.duration) * 100);
  }

  get currentTimeLabel(): string {
    return this.formatTime(this.currentTime);
  }

  get durationLabel(): string {
    return this.formatTime(this.duration);
  }

  get playIcon(): LucideIconInput {
    return this.isPlaying ? LucidePause : LucidePlay;
  }

  get playLabelKey(): string {
    return this.isPlaying ? 'translations.global.ariaLabels.pauseAudio' : 'translations.global.ariaLabels.playAudio';
  }

  get isSilent(): boolean {
    return this.isMuted || this.volume === 0;
  }

  get volumeIcon(): LucideIconInput {
    if (this.isSilent) {
      return LucideVolumeX;
    }
    if (this.volume < 0.5) {
      return LucideVolume1;
    }
    return LucideVolume2;
  }

  get muteLabelKey(): string {
    return this.isSilent ? 'translations.global.ariaLabels.unmuteAudio' : 'translations.global.ariaLabels.muteAudio';
  }

  get volumeSliderValue(): number {
    return this.isSilent ? 0 : this.volume;
  }

  get volumePct(): number {
    return this.volumeSliderValue * 100;
  }

  togglePlay(): void {
    const audio = this.audioElementRef?.nativeElement;
    if (!audio) {
      return;
    }

    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  }

  pause(): void {
    this.audioElementRef?.nativeElement.pause();
  }

  onSeekInput(event: Event): void {
    const audio = this.audioElementRef?.nativeElement;
    if (!audio || !this.canSeek) {
      return;
    }

    const slider = event.target as HTMLInputElement;
    audio.currentTime = Number(slider.value);
  }

  toggleMute(): void {
    const audio = this.audioElementRef?.nativeElement;
    if (!audio) {
      return;
    }

    if (!this.isSilent) {
      audio.muted = true;
      return;
    }

    // Unmuting after the slider was dragged to zero would stay silent, so bring the volume back up.
    if (audio.volume === 0) {
      audio.volume = 1;
    }
    audio.muted = false;
  }

  onVolumeInput(event: Event): void {
    const audio = this.audioElementRef?.nativeElement;
    if (!audio) {
      return;
    }

    const slider = event.target as HTMLInputElement;
    const volume = Number(slider.value);
    audio.volume = volume;
    audio.muted = volume === 0;
  }

  onVolumeChange(): void {
    const audio = this.audioElementRef?.nativeElement;
    if (!audio) {
      return;
    }

    this.volume = audio.volume;
    this.isMuted = audio.muted;
  }

  onPlay(): void {
    this.isPlaying = true;
  }

  onPause(): void {
    this.isPlaying = false;
  }

  onEnded(): void {
    this.isPlaying = false;
  }

  onTimeUpdate(): void {
    this.currentTime = this.audioElementRef?.nativeElement.currentTime ?? 0;
  }

  onLoadedMetadata(): void {
    const audioDuration = this.audioElementRef?.nativeElement.duration;
    this.duration = audioDuration && isFinite(audioDuration) ? audioDuration : 0;
  }

  private formatTime(totalSeconds: number): string {
    if (!totalSeconds || !isFinite(totalSeconds)) {
      return '0:00';
    }

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
